import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import {
  SubmitHandlerWithDirtyFields,
  buildDirtyObject,
} from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getTriggerMessageDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/ocpp/trigger-message.form-default-values';
import { TriggerMessageModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/ocpp/trigger-message.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useCpmsBoTriggerMessageOcppMutation } from '@app/core/service/cpms-bo-charge-point-ocpp.service';

import {
  CreateChargePointOcppTriggerMessageContextProvider,
  useChargePointContext,
} from '../charge-point.context';
import { ChargePointOcppTriggerMessageCreateFormController } from './charge-point-ocpp-trigger-message-create-form/charge-point-ocpp-trigger-message-create-form.controller';

interface ChargePointOcppTriggerMessageCreateControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const ChargePointOcppTriggerMessageCreateController = ({
  onCancel,
  onSuccess,
}: ChargePointOcppTriggerMessageCreateControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const chargePoint = useChargePointContext();
  const [triggerMessageOcppMutation] = useCpmsBoTriggerMessageOcppMutation();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<
    Pick<
      TriggerMessageModel,
      'chargePointId' | 'requestedMessage' | 'physicalSocketId'
    >
  > = (errors) => {
    Logger.error(errors);
    toast.error(
      t('$.charge-point-ocpp-trigger-message-create.error', {
        error: t('global.unknown'),
      }),
    );
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    Pick<
      TriggerMessageModel,
      'chargePointId' | 'requestedMessage' | 'physicalSocketId'
    >
  > = (fieldValues, _, dirtyFields) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    const dirtyObject = buildDirtyObject(dirtyFields, fieldValues);

    if (!dirtyObject.requestedMessage) {
      setMultiStepFormState(MultiStepFormState.Default);
      return;
    }

    triggerMessageOcppMutation({
      chargePointId: fieldValues.chargePointId,
      requestedMessage: dirtyObject.requestedMessage,
      physicalSocketId: dirtyObject.physicalSocketId,
    })
      .unwrap()
      .then(() => {
        toast.success(t('$.charge-point-ocpp-trigger-message-create.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(
          t('$.charge-point-ocpp-trigger-message-create.error', {
            error: errorMessage,
          }),
        );
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getTriggerMessageDefaultValues(chargePoint.id);

  return (
    <CreateChargePointOcppTriggerMessageContextProvider data={data}>
      <ChargePointOcppTriggerMessageCreateFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </CreateChargePointOcppTriggerMessageContextProvider>
  );
};
